import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus } from "lucide-react";
import { FAQ_CATEGORIES as CATEGORIES } from "../data/faq";
import { faqPageSchema } from "@/lib/seo/schemas";
import { safeJsonLd } from "@/lib/seo/safeJson";

const fade = {
  hidden: { opacity: 0, y: 20 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.05, duration: 0.7, ease: [0.19, 1, 0.22, 1] },
  }),
};

export default function FAQ() {
  const [openId, setOpenId] = useState(null);

  const faqLd = faqPageSchema(
    CATEGORIES.flatMap((c) => c.items.map((it) => ({ question: it.q, answer: it.a })))
  );

  return (
    <div
      data-testid="faq-page"
      className="relative min-h-screen bg-[#0a0a0c] pt-32 lg:pt-40"
    >
      <script
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: safeJsonLd(faqLd) }}
      />
      {/* Hero */}
      <section className="mx-auto max-w-[1200px] px-6 pb-16 lg:px-12">
        <motion.p
          initial="hidden"
          animate="show"
          variants={fade}
          className="font-tech text-[11px] uppercase tracking-[0.4em] text-[#c6a87c]"
        >
          Help Centre
        </motion.p>
        <motion.h1
          initial="hidden"
          animate="show"
          variants={fade}
          custom={1}
          className="mt-6 font-serif-display text-4xl leading-[1.05] text-white sm:text-5xl lg:text-6xl"
          data-testid="faq-heading"
        >
          Frequently asked questions.
        </motion.h1>
        <motion.p
          initial="hidden"
          animate="show"
          variants={fade}
          custom={2}
          className="mt-8 max-w-2xl font-body text-base leading-relaxed text-white/60 sm:text-lg"
        >
          The answers we hear ourselves saying most often — from lead times and pricing through
          to coastal specifications, glazing and warranty. Anything missing? Ask us directly.
        </motion.p>

        {/* Anchor jump nav */}
        <motion.div
          initial="hidden"
          animate="show"
          variants={fade}
          custom={3}
          className="mt-10 flex flex-wrap gap-3"
        >
          {CATEGORIES.map((c) => (
            <a
              key={c.id}
              href={`#${c.id}`}
              data-testid={`faq-jump-${c.id}`}
              className="rounded-full border border-white/15 px-5 py-2.5 font-tech text-[10px] uppercase tracking-[0.24em] text-white/75 transition-colors hover:border-[#c6a87c] hover:text-[#c6a87c]"
            >
              {c.label}
            </a>
          ))}
        </motion.div>
      </section>

      <div className="mx-auto max-w-[1200px] border-t border-white/10" />

      {/* Categories */}
      {CATEGORIES.map((cat, catIdx) => (
        <section
          key={cat.id}
          id={cat.id}
          data-testid={`faq-section-${cat.id}`}
          className="mx-auto max-w-[1200px] scroll-mt-32 px-6 py-20 lg:px-12"
        >
          <div className="grid gap-14 lg:grid-cols-[280px_1fr]">
            <div className="lg:sticky lg:top-32 lg:self-start">
              <p className="font-tech text-[10px] uppercase tracking-[0.3em] text-[#c6a87c]/80">
                Section 0{catIdx + 1}
              </p>
              <h2 className="mt-4 font-serif-display text-3xl leading-tight text-white lg:text-4xl">
                {cat.label}
              </h2>
            </div>

            <div className="divide-y divide-white/10 border-t border-white/10">
              {cat.items.map((item, i) => {
                const id = `${cat.id}-${i}`;
                const isOpen = openId === id;
                return (
                  <div
                    key={id}
                    data-testid={`faq-item-${cat.id}-${i}`}
                    className="group"
                  >
                    <button
                      onClick={() => setOpenId(isOpen ? null : id)}
                      data-testid={`faq-toggle-${cat.id}-${i}`}
                      aria-expanded={isOpen}
                      className="flex w-full items-start justify-between gap-6 py-7 text-left transition-colors hover:text-[#c6a87c]"
                    >
                      <span
                        className={`font-serif-display text-lg leading-snug transition-colors lg:text-xl ${
                          isOpen ? "text-[#c6a87c]" : "text-white"
                        }`}
                      >
                        {item.q}
                      </span>
                      <motion.span
                        animate={{ rotate: isOpen ? 45 : 0 }}
                        transition={{ duration: 0.4, ease: [0.19, 1, 0.22, 1] }}
                        className={`mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border transition-colors ${
                          isOpen
                            ? "border-[#c6a87c] text-[#c6a87c]"
                            : "border-white/20 text-white/60 group-hover:border-[#c6a87c] group-hover:text-[#c6a87c]"
                        }`}
                      >
                        <Plus size={14} strokeWidth={1.4} />
                      </motion.span>
                    </button>
                    <AnimatePresence initial={false}>
                      {isOpen && (
                        <motion.div
                          key="a"
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.45, ease: [0.19, 1, 0.22, 1] }}
                          className="overflow-hidden"
                        >
                          <p
                            data-testid={`faq-answer-${cat.id}-${i}`}
                            className="max-w-3xl pb-7 pr-14 font-body text-[15px] leading-[1.8] text-white/70"
                          >
                            {item.a}
                          </p>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      ))}

      {/* CTA */}
      <section className="mx-auto max-w-[1200px] border-t border-white/10 px-6 py-20 lg:px-12">
        <div className="flex flex-col items-start justify-between gap-8 md:flex-row md:items-center">
          <div>
            <p className="font-tech text-[10px] uppercase tracking-[0.3em] text-white/40">
              Still have questions?
            </p>
            <h3 className="mt-4 max-w-xl font-serif-display text-2xl leading-tight text-white lg:text-3xl">
              We'd rather answer a specific question than guess at a generic one — drop us a line.
            </h3>
          </div>
          <a
            href="mailto:isaiah@euro-steel.co.nz?subject=FAQ%20Question"
            data-testid="faq-contact-btn"
            className="whitespace-nowrap rounded-full bg-[#c6a87c] px-8 py-3 font-tech text-[11px] uppercase tracking-[0.2em] text-[#0a0a0c] transition-all duration-500 hover:bg-[#d8bd93]"
          >
            Contact Us
          </a>
        </div>
      </section>
    </div>
  );
}
